import { CheckCircleIcon } from "@/Icons/CheckCircle";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { Button } from "@/components/button";
import { Translate, useTrans } from "@/translations";

import { useContext, useEffect, useMemo, useState } from "react";

import { ImportContext } from "../ImportContext";
import { SuccessTextProps } from "../types";

export const AddingToSystem = () => {
  const { t } = useTrans();
  const context = useContext(ImportContext);

  const { loadingState, validationState, selectedTemplate, addToSystem } =
    context;

  const [counts, setCounts] = useState<any>([]);
  const [successText, setSuccessText] = useState<SuccessTextProps>({
    addedText: "",
    findResultsText: "",
    findResultsLink: "",
  });

  // doing it like this need to preserve counts after data wipe on successful upload
  useEffect(() => {
    if (Object.keys(validationState).length) {
      const values = Object.keys(validationState)
        .filter((key) => validationState[key])
        .map((key) => {
          const section = selectedTemplate?.mappingSections.find(
            (section) => section.key === key,
          );

          if (section?.hideCount) return <></>;

          const label = section?.title;
          const firstColumn = Object.keys(validationState[key]!)[0];
          const count = validationState[key]![firstColumn]?.length;

          return (
            <div className="mb-2 text-center text-base font-normal" key={label}>
              <span className="font-medium">{t(label || "")}:</span> {count}
            </div>
          );
        });
      setCounts(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationState]);

  useEffect(() => {
    if (selectedTemplate) {
      setSuccessText({
        addedText: selectedTemplate.addedText,
        findResultsText: selectedTemplate.findResultsText,
        findResultsLink: selectedTemplate.findResultsLink,
      });
    }
  }, [selectedTemplate]);

  const loadingView = useMemo(() => {
    if (loadingState.importing) {
      return (
        <div className="flex h-full flex-col items-center justify-center">
          <LoadingIndicator />
          <div className="mt-6 text-2xl font-medium">
            {t(loadingState.importing)}
          </div>
        </div>
      );
    }

    const hasFailedEvents =
      loadingState.failedItems && loadingState.failedItems.length > 0;

    if (loadingState.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center">
          <div className="text-carrot-red text-2xl font-medium">
            {t("importLabels:importFailed")}
          </div>

          {hasFailedEvents && (
            <div className="mt-6 max-h-48 w-full max-w-lg overflow-y-auto rounded border border-red-200 bg-red-50 p-4">
              <ul className="space-y-1 text-sm text-red-600">
                {loadingState.failedItems?.map(({ index, message }) => (
                  <li key={index}>
                    {index} : {message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hasFailedEvents && (
            <>
              <div className="mt-8 text-base">{t("importLabels:tryAgain")}</div>
              <Button
                label="actions:tryAgain"
                type="button"
                color="primary"
                onClick={() => addToSystem(context)}
                className="my-8"
              />
            </>
          )}

          <div className="mt-5">
            <Translate
              i18nKey={"importLabels:contactSupport"}
              components={[
                // Anchor content is added by the translation
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a href="mailto:support@carrot.tech" className="underline" />,
              ]}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-5/6 flex-col items-center justify-center">
        <CheckCircleIcon width={32} height={32} />
        <div className="text-carrot-green mt-4 text-2xl font-medium">
          {t("importLabels:wellDone")}
        </div>
        <div className="mt-8 text-base font-normal">
          {t(successText.addedText)}
        </div>

        <div className="mt-8">{counts}</div>

        <div className="mt-8 max-w-[650px] text-base font-normal whitespace-pre-wrap">
          <Translate
            i18nKey={successText.findResultsText}
            components={[
              // eslint-disable-next-line
              <a href={successText.findResultsLink} className="underline" />,
            ]}
          />
        </div>
      </div>
    );
  }, [loadingState, t, addToSystem, context, counts, successText]);

  return loadingView;
};
